/* eslint-disable react-refresh/only-export-components */
import { formatDate, ImgSrc } from "@/utils";
import { Done, DoneAll } from "@mui/icons-material";
import { Box, capitalize, Stack, Typography } from "@mui/material";
import { FC } from "react";
import { ChatMessageProps } from "./chat-message.types";
import { PhotoProvider, PhotoView } from "react-photo-view";
import MuxPlayer from "@mux/mux-player-react";

const InnerFeedback: FC<
  Pick<ChatMessageProps, "message" | "createdAt" | "read" | "feedback">
> = ({ read, message, createdAt, feedback }) => (
  <Stack direction="row" spacing={4} alignItems="flex-end">
    <Stack spacing={1}>
      <Stack>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ wordBreak: "break-all" }}
        >
          Subject ({capitalize(feedback?.type ?? "")})
        </Typography>
        <Typography variant="body2">{feedback?.subject}</Typography>
      </Stack>
      <Stack>
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ wordBreak: "break-all" }}
        >
          Message
        </Typography>
        <Typography variant="body2">{message}</Typography>
      </Stack>
    </Stack>

    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography variant="caption" component="span" sx={{ lineHeight: 1 }}>
        {formatDate(createdAt, "HH:MM")}
      </Typography>

      {read ? <DoneAll fontSize="small" /> : <Done fontSize="small" />}
    </Stack>
  </Stack>
);

const InnerText: FC<
  Pick<ChatMessageProps, "message" | "createdAt" | "read">
> = ({ read, createdAt, message }) => (
  <Box
    sx={{
      width: "100%",
      display: "inline-block",
      position: "relative",
    }}
  >
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-end",
        justifyContent: "space-between",
        columnGap: 4,
        rowGap: 0.5,
      }}
    >
      <Typography
        variant="body2"
        sx={{ flex: "1 1 auto", wordBreak: "break-all" }}
      >
        {message}
      </Typography>

      <Box
        sx={{
          flex: "0 0 auto",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          marginLeft: "auto",
        }}
      >
        <Typography variant="caption" component="span" sx={{ lineHeight: 1 }}>
          {formatDate(createdAt, "HH:MM")}
        </Typography>

        {read ? <DoneAll fontSize="small" /> : <Done fontSize="small" />}
      </Box>
    </Box>
  </Box>
);

const InnerAttachment: FC<
  Pick<ChatMessageProps, "message" | "createdAt" | "read" | "attachment">
> = ({ message, read, createdAt, attachment }) => (
  <Stack spacing={1}>
    {!!attachment && (
      <Stack sx={{ minWidth: "100%" }}>
        {attachment.type === "image" && (
          <PhotoProvider>
            <PhotoView src={ImgSrc(attachment.path)}>
              <img
                style={{
                  maxWidth: "100%",
                  maxHeight: 400,
                  borderRadius: 4,
                  cursor: "pointer",
                }}
                src={ImgSrc(attachment.path)}
              />
            </PhotoView>
          </PhotoProvider>
        )}

        {attachment.type === "video" && (
          <MuxPlayer className="video" src={attachment.url} />
        )}
      </Stack>
    )}

    <Box
      sx={{
        width: "100%",
        display: "inline-block",
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="body2"
          sx={{ flex: "1 1 auto", wordBreak: "break-all" }}
        >
          {message}
        </Typography>
        <Box
          sx={{
            flex: "0 0 auto",
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            marginLeft: "auto",
          }}
        >
          <Typography variant="caption" component="span" sx={{ lineHeight: 1 }}>
            {formatDate(createdAt, "HH:MM")}
          </Typography>

          {read ? <DoneAll fontSize="small" /> : <Done fontSize="small" />}
        </Box>
      </Box>
    </Box>
  </Stack>
);

export const InnerMessage = {
  Feedback: InnerFeedback,
  Text: InnerText,
  Attachment: InnerAttachment,
};
